import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SmoothCollapse from 'react-smooth-collapse';
import CaretRightIcon from 'calcite-ui-icons-react/CaretRightIcon';
import UserIcon from 'calcite-ui-icons-react/UserIcon';
import AnnotateToolIcon from 'calcite-ui-icons-react/AnnotateToolIcon';

import { sortItems } from '../../../utils/sort.utils';

import Loader from './../loader';

import AnnotationLayer from './layer-components/annotation-layer';
import HansenLayer from './layer-components/hansen-layer';
import HydrologyLayer from './layer-components/hydrology-layer';
import MapLayer from './layer-components/map-layer';
import MyLayer from './layer-components/my-layer';
import PlanetLayer from './layer-components/planet-layer';
import SatelliteLayer from './layer-components/statellite-layer';
import TerrainLayer from './layer-components/terrain-layer';
import TreeCoverLayer from './layer-components/tree-cover-layer';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import MapController from '../../../controllers/MapController';

import { RootState } from '../../../reducers/app';

import './map-layers.scss';
import { setSwipeToolActive, setTimeSlidersEnabled } from '../../../reducers/mapview/actions';

const NoSubGroups = ({
  icon,
  group,
  title,
  sectionSubtitle,
  allowOverflow,
}: {
  icon: string;
  group: string;
  title: string;
  sectionSubtitle: string;
  allowOverflow: boolean;
}) => {
  const [featureLayersOpen, setFeatureLayersOpen] = useState(true);
  const [selectedPopover, setSelectedPopover] = useState('');

  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);

  const swipeToolActive = useSelector((state: RootState) => state.mapview.swipeToolActive);
  const timelineOpen = useSelector((state: RootState) => state.auditdata.timelineOpen);
  const timeSlidersEnabled = useSelector((state: RootState) => state.mapview.timeSlidersEnabled);

  const dispatch = useDispatch();

  const renderMapLayers = (group: string) => {
    return MapController.mapLayers.map((layer, key) => {
      if (layer.group !== group) return '';
      if (layer.id === 'hansen') {
        return <HansenLayer key={key} layer={layer} />;
      } else if (layer.group === 'imagery') {
        return (
          <SatelliteLayer
            key={key}
            layer={layer}
            setSelectedPopover={setSelectedPopover}
            selectedPopover={selectedPopover}
          />
        );
      } else if (layer.id === 'hydrology') {
        return <HydrologyLayer layer={layer} />;
      } else if (layer.id === 'tree-cover-loss-by-dominant-driver') {
        return <TreeCoverLayer key={key} layer={layer} />;
      } else if (layer.id === 'terrain') {
        return <TerrainLayer key={key} layer={layer} />;
      } else {
        return <MapLayer key={key} layer={layer} />;
      }
    });
  };

  useEffect(() => {
    if (selectedPopover === 'landsat' || selectedPopover === 'sentinel') {
      if (swipeToolActive) {
        dispatch(setSwipeToolActive(false));
      }
      const otherImageryLayer = selectedPopover === 'landsat' ? 'sentinel' : 'landsat';
      if (visibleLayers.includes(otherImageryLayer)) {
        MapController.updateLayerVisibility(otherImageryLayer, false);
      }

      const layer = MapController._map?.allLayers.find((l) => l.id === selectedPopover);
      if (!layer?.visible) {
        MapController.updateLayerVisibility(selectedPopover, true);
      }

      if (selectedPopover === 'landsat') {
        MapController._updatingLandsatTimeslider = true;
      } else {
        MapController._updatingSentinelTimeslider = true;
      }
      MapController.debouncedUpdateTimeSliders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPopover]);

  useEffect(() => {
    if (!timeSlidersEnabled && (selectedPopover === 'landsat' || selectedPopover === 'sentinel')) {
      setSelectedPopover('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlidersEnabled]);

  useEffect(() => {
    if (swipeToolActive || timelineOpen) {
      if (selectedPopover === 'landsat' || selectedPopover === 'sentinel') {
        setSelectedPopover('');
        dispatch(setTimeSlidersEnabled(false));
      }
    } else if (
      MapController._mapview &&
      MapController._mapview?.extent!.height <= MapController._timesliderExtentLimit
    ) {
      dispatch(setTimeSlidersEnabled(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeToolActive, timelineOpen]);

  return (
    <div className={`map-layers__section ${allowOverflow ? 'overflow' : ''}`} data-tut='step-twelve'>
      <div className='section-container' onClick={() => setFeatureLayersOpen(!featureLayersOpen)}>
        <div className='section-icons'>
          <CaretRightIcon className={featureLayersOpen ? 'open' : ''} size={12} />
          {icon && <span className={icon} />}
          <p className='title'>{title}</p>
        </div>
        <p className='section-subtitle'>{sectionSubtitle}</p>
      </div>
      <SmoothCollapse className='featured-layers-container' expanded={featureLayersOpen} allowOverflowWhenOpen>
        {loaderActive && <Loader />}
        {group === 'imagery' && <PlanetLayer />}
        {renderMapLayers(group)}
      </SmoothCollapse>
    </div>
  );
};

const SubGroup = ({
  group,
  subGroup,
  title,
  isOpen,
  setOpen,
}: {
  group: string;
  subGroup: string;
  title: string;
  isOpen: boolean;
  setOpen: Function;
}) => {
  // const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);

  const renderMapLayers = (group: string, subGroup: string) => {
    const mapSubLayers = MapController.mapLayers.filter((lay) => lay.subgroup === subGroup && lay.group === group);
    return mapSubLayers.map((layer, key) => {
      if (layer.id === 'hansen') {
        return <HansenLayer key={key} layer={layer} />;
      } else if (layer.id === 'hydrology') {
        return <HydrologyLayer key={key} layer={layer} />;
      } else if (layer.id === 'tree-cover-loss-by-dominant-driver') {
        return <TreeCoverLayer key={key} layer={layer} />;
      } else if (layer.id === 'terrain') {
        return <TerrainLayer key={key} layer={layer} />;
      } else {
        return <MapLayer key={key} layer={layer} />;
      }
    });
  };

  return (
    <div>
      <div className='subgroup'>
        <div className='section-icons'>
          <p className='title'>{title}</p>
          <span className={`esri-icon-down ${isOpen ? 'open' : ''}`} onClick={() => setOpen(!isOpen)} />
        </div>
      </div>
      <SmoothCollapse className='featured-layers-container' expanded={isOpen} allowOverflowWhenOpen>
        {isOpen && renderMapLayers(group, subGroup)}
      </SmoothCollapse>
    </div>
  );
};

const LayersSection = ({
  icon,
  title,
  sectionSubtitle,
  isOpen,
  setOpen,
  children,
}: {
  icon: string;
  title: string;
  sectionSubtitle: string;
  isOpen: Boolean;
  setOpen: Function;
  children: React.ReactNode;
}) => {
  return (
    <div className='map-layers__section' data-tut='step-twelve'>
      <div className='section-container' onClick={() => setOpen(!isOpen)}>
        <div className='section-icons'>
          <CaretRightIcon className={isOpen ? 'open' : ''} size={12} />
          <span className={icon} />
          <p className='title'>{title}</p>
        </div>
        <p className='section-subtitle'>{sectionSubtitle}</p>
      </div>
      {children}
    </div>
  );
};

/**
 * Component enabling a user to interact with their map layers
 */
const MapLayers = ({ mapLayersToggled }: { mapLayersToggled: boolean }) => {
  // LOCAL STATE
  // const [activeSection, setActiveSection] = useState('');
  const [myLayersOpen, setMyLayersOpen] = useState(false);
  const [annotationLayersOpen, setAnnotationLayersOpen] = useState(false);
  const [analysisLayersOpen, setAnalysisLayersOpen] = useState(false);
  const [overflow, setOverflow] = useState('');

  // subgroup layer group states
  const [globalEcoLayers, setGlobalEcoLayers] = useState(false);
  const [regionalEcoLayers, setRegionalEcoLayers] = useState(false);
  const [humanLayers, setHumanLayers] = useState(false);

  // REDUX
  const myLayers = useSelector((state: RootState) => state.mapview.myLayers);
  const annotations = useSelector((state: RootState) => state.mapview.annotations);

  useEffect(() => {
    if (myLayers.length > 0 && myLayers[myLayers.length - 1].id.includes('-raster-analysis') && !myLayersOpen) {
      setMyLayersOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLayers, myLayers.length]);

  /**
   * render function to show annotations or a placeholder when there aren't annotations
   */
  const renderAnnotations = () => {
    if (annotations.length) {
      return annotations
        .sort((a, b) => {
          return (
            sortItems(a.attributes.title.toLowerCase(), b.attributes.title.toLowerCase()) ||
            sortItems(a.attributes.id, b.attributes.id)
          );
        })
        .map((annotation) => <AnnotationLayer annotation={annotation} />);
    } else {
      return <div className='no-layers'>Add Annotations to the map!</div>;
    }
  };

  const getListStyle = (isDraggingOver: any) => ({
    background: isDraggingOver ? '#9BD3DD' : '',
  });

  const isMyLayersEmpty =
    !myLayers.length || (myLayers.length === 1 && myLayers[0].id.includes('auditArea') && !myLayers[0].graphics.length);

  return (
    <div className={`map-layers-container ${mapLayersToggled ? 'open' : 'closed'} ${overflow}`}>
      <div className='topo-img' />
      <div className='map-layers__section'>
        <div className='section-container large' onClick={() => setMyLayersOpen(!myLayersOpen)}>
          <div className='section-icons'>
            <CaretRightIcon className={myLayersOpen ? 'open' : ''} size={12} />
            <UserIcon size={18} />
            <p className='title'>My Layers</p>
          </div>
          <p className='section-subtitle'>
            user created layers, either as uploaded additional areas or results from analysis
          </p>
        </div>
        <SmoothCollapse className='my-layers-container' expanded={myLayersOpen} allowOverflowWhenOpen>
          <DragDropContext onDragEnd={(e) => MapController.reorderMyLayers(e, myLayers)}>
            {
              <Droppable droppableId='droppable'>
                {(provided, snapshot) => (
                  <div
                    className='dataset-present'
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                  >
                    {!isMyLayersEmpty ? (
                      myLayers.map((layer: any, index: number) => {
                        const layerHasGraphics =
                          (layer.hasOwnProperty('graphics') && layer.graphics.length > 0) ||
                          !layer.hasOwnProperty('graphics');
                        if (!layerHasGraphics) return null;
                        else {
                          return (
                            <Draggable key={layer.id} draggableId={layer.id} index={index}>
                              {(providedDraggable, snapshotDraggable) => (
                                <MyLayer
                                  setOverflow={setOverflow}
                                  layer={layer}
                                  provided={providedDraggable}
                                  snapshot={snapshotDraggable}
                                />
                              )}
                            </Draggable>
                          );
                        }
                      })
                    ) : (
                      <div className='no-layers'>Add custom layers to the map using the "Contextual Data" panel.</div>
                    )}
                  </div>
                )}
              </Droppable>
            }
          </DragDropContext>
        </SmoothCollapse>
      </div>
      <NoSubGroups
        icon='esri-icon-maps'
        group='key-layers'
        title='Key Layers'
        sectionSubtitle='authoritative layers directly pertaining to the audit analysis'
        allowOverflow={false}
      />

      <LayersSection
        icon='esri-icon-applications'
        sectionSubtitle='layers which can actively supplement the audit with data'
        title='Analysis Layers'
        isOpen={analysisLayersOpen}
        setOpen={setAnalysisLayersOpen}
      >
        <SmoothCollapse className='featured-layers-container' expanded={analysisLayersOpen} allowOverflowWhenOpen>
          {analysisLayersOpen && (
            <div className='featured-layers-container'>
              <SubGroup
                group='analysis-layers'
                subGroup='global-eco-layers'
                title='Global Ecological Layers'
                isOpen={globalEcoLayers}
                setOpen={setGlobalEcoLayers}
              />
              <SubGroup
                group='analysis-layers'
                subGroup='regional-eco-layers'
                title='Regional Ecological Layers'
                isOpen={regionalEcoLayers}
                setOpen={setRegionalEcoLayers}
              />
              <SubGroup
                group='analysis-layers'
                subGroup='human-layers'
                title='Human Layers'
                isOpen={humanLayers}
                setOpen={setHumanLayers}
              />
            </div>
          )}
        </SmoothCollapse>
      </LayersSection>
      <NoSubGroups
        icon='esri-icon-layers'
        group='reference-layers'
        title='Reference Layers'
        sectionSubtitle='layers providing passive contextual data'
        allowOverflow={false}
      />
      <NoSubGroups
        icon='esri-icon-collection'
        group='imagery'
        title='Imagery'
        sectionSubtitle='Global satellite images providing additional context and analysis'
        allowOverflow={true}
      />

      <div className='map-layers__section'>
        <div
          className={`section-container ${annotationLayersOpen ? 'open' : ''}`}
          onClick={() => setAnnotationLayersOpen(!annotationLayersOpen)}
        >
          <div className='section-icons'>
            <CaretRightIcon className={annotationLayersOpen ? 'open' : ''} size={12} />
            <AnnotateToolIcon size={18} />
            <p className='title'>My Annotations</p>
          </div>
          <p className='section-subtitle'>graphics made from user inputs and selections</p>
        </div>
        <SmoothCollapse className='featured-layers-container' expanded={annotationLayersOpen} allowOverflowWhenOpen>
          {renderAnnotations()}
        </SmoothCollapse>
      </div>
    </div>
  );
};

export default MapLayers;
